/**
 * exportApi — frontend client for the document-export service (TASK-406+).
 *
 * Used by:
 *   - PostMeetingHub/ExecutiveSummary.tsx (download-only brief path; preview=false)
 *   - meeting/preparation/BriefTab.tsx (preview-first brief path; preview=true)
 *   - meeting/preparation/PreparationPage.tsx (debounced brief regenerate after chat-Apply)
 *   - portfolio/atoms/ExportDropdown.tsx (TASK-407 portfolio PPTX path)
 *   - meeting/preparation/PortfolioTab.tsx (TASK-407 preview-first portfolio path)
 *
 * Backend contract (see backend/services/document-export/api/routes.py):
 *   POST /api/exports/pptx/brief/{session_id}?preview=<bool>
 *     body: { language, regenerate, extra? }
 *     → 200 ExportResponse on cache hit OR generation complete
 *     → 202 PendingBriefResponse if no brief content exists yet
 *
 *   POST /api/exports/pptx/portfolio/{client_id}?preview=<bool>            ← TASK-407
 *     body: { portfolio_id?, as_of_date?, language, view, regenerate, session_id?, client_name? }
 *     → 200 ExportResponse
 *     → 400 MultiplePortfoliosError when client has >1 portfolio and none specified
 *
 *   GET  /api/exports/pptx/brief/{session_id}/latest?language=en           → ExportResponse | null
 *   GET  /api/exports/pptx/portfolio/{client_id}/latest?language=en        → ExportResponse | null  ← TASK-407
 *
 *   GET  /api/exports/{export_id}/download  → 302 → presigned PPTX URL
 *   GET  /api/exports/{export_id}/preview   → 302 → presigned PDF URL
 *   GET  /api/exports/{export_id}/status    → ExportResponse
 */

import { httpClient, httpClientLongRunning } from '../../lib/httpClient';

export type ExportStatus =
  | 'pending'
  | 'generating'
  | 'rendering'
  | 'ready'
  | 'failed'
  | 'superseded'
  | 'pending_brief';

export interface ExportResponse {
  export_id: string;
  status: ExportStatus;
  language: string;
  version: number;
  pptx_url?: string | null;
  pdf_url?: string | null;
  generated_at?: string | null;
  error_message?: string | null;
  degraded_sections?: string[];
  message?: string | null;
}

export interface PendingBriefResponse {
  status: 'pending_brief';
  session_id: string;
  polling_url: string;
  message: string;
}

export interface BriefExportOptions {
  language?: 'en' | 'de' | 'fr' | 'it';
  regenerate?: boolean;
  preview?: boolean;
  signal?: AbortSignal;
}

export class ExportPendingBriefError extends Error {
  readonly response: PendingBriefResponse;
  constructor(response: PendingBriefResponse) {
    super(response.message || 'No brief content available yet');
    this.name = 'ExportPendingBriefError';
    this.response = response;
  }
}

function isPending(value: ExportResponse | PendingBriefResponse): value is PendingBriefResponse {
  return value?.status === 'pending_brief';
}

/**
 * Generate (or fetch cached) a brief PPTX export. Preview=true also renders
 * a PDF rendition for in-browser display.
 *
 * Throws `ExportPendingBriefError` when the backend returned 202 (no brief
 * content exists yet for the session — advisor needs to generate the brief
 * first).
 */
export async function getOrCreateBriefExport(
  sessionId: string,
  options: BriefExportOptions = {}
): Promise<ExportResponse> {
  const { language = 'en', regenerate = false, preview = false, signal } = options;
  const url = `/api/exports/pptx/brief/${encodeURIComponent(sessionId)}?preview=${preview}`;
  const result = await httpClient.post<ExportResponse | PendingBriefResponse>(
    url,
    { language, regenerate },
    { signal }
  );
  if (isPending(result)) {
    throw new ExportPendingBriefError(result);
  }
  return result;
}

/** Returns the current ready export for this (session, language) or null. */
export async function getBriefExportLatest(
  sessionId: string,
  language: string = 'en'
): Promise<ExportResponse | null> {
  const url = `/api/exports/pptx/brief/${encodeURIComponent(
    sessionId
  )}/latest?language=${encodeURIComponent(language)}`;
  return await httpClient.get<ExportResponse | null>(url);
}

/** TASK-606: one export row for the Files tab (status honesty + Retry). */
export interface ExportListItem {
  id: string;
  export_type: 'brief' | 'portfolio' | 'finsa' | 'workbench';
  session_id: string | null;
  client_id: string | null;
  status: 'pending' | 'generating' | 'rendering' | 'ready' | 'failed' | 'superseded';
  error_message: string | null;
  language: string;
  version: number;
  format?: string;
  /** TASK-741: the workbench source — "based on vN" derives from these. */
  source_document_id?: string | null;
  source_document_version?: number | null;
  created_at: string | null;
  completed_at: string | null;
}

/** TASK-606: list recent exports (optionally by status, e.g. 'failed'). */
export async function listExports(options: { status?: string; limit?: number } = {}): Promise<{
  exports: ExportListItem[];
  total: number;
}> {
  const params = new URLSearchParams();
  if (options.status) params.set('status', options.status);
  if (options.limit) params.set('limit', String(options.limit));
  const qs = params.toString();
  return httpClient.get(`/api/exports${qs ? `?${qs}` : ''}`);
}

/**
 * TASK-606: retry a failed export by re-invoking its original generator with
 * regenerate=true (the scope — session/client + type + language — is on the row).
 */
export async function retryExport(item: ExportListItem): Promise<unknown> {
  const language = (['en', 'de', 'fr', 'it'].includes(item.language) ? item.language : 'en') as
    | 'en'
    | 'de'
    | 'fr'
    | 'it';
  if (item.export_type === 'brief' && item.session_id) {
    return getOrCreateBriefExport(item.session_id, { language, regenerate: true });
  }
  if (item.export_type === 'finsa' && item.session_id) {
    return createFinsaExport(item.session_id, language);
  }
  if (item.export_type === 'portfolio' && item.client_id) {
    const url = `/api/exports/pptx/portfolio/${encodeURIComponent(item.client_id)}?preview=false`;
    return httpClientLongRunning.post<ExportResponse>(url, {
      language,
      regenerate: true,
      view: 'client',
    });
  }
  // TASK-741: a failed workbench deliverable re-exports its pinned source version.
  if (
    item.export_type === 'workbench' &&
    item.source_document_id &&
    item.source_document_version != null
  ) {
    const { exportDocumentVersion } = await import('./documentApi');
    return exportDocumentVersion(
      item.source_document_id,
      item.source_document_version,
      item.format === 'pdf' ? 'pdf' : 'same',
    );
  }
  return null;
}

export async function getExportStatus(exportId: string): Promise<ExportResponse> {
  return await httpClient.get<ExportResponse>(`/api/exports/${encodeURIComponent(exportId)}/status`);
}

/** Statuses from which an export will not move on its own. */
const TERMINAL_EXPORT_STATUSES: ReadonlySet<ExportStatus> = new Set<ExportStatus>([
  'ready',
  'failed',
  'superseded',
]);

export function isTerminalExportStatus(status: ExportStatus): boolean {
  return TERMINAL_EXPORT_STATUSES.has(status);
}

/**
 * The 202 body `create_portfolio_export` returns when a generation for the same
 * client is already in flight. It is NOT an `ExportResponse` — it carries
 * `in_flight_export_id` and no `export_id`, no `pptx_url`, no `version`.
 */
export interface GenerationInFlightResponse {
  status: 'generating';
  in_flight_export_id?: string | null;
  message?: string | null;
}

export function isGenerationInFlight(
  response: ExportResponse | GenerationInFlightResponse
): response is GenerationInFlightResponse {
  return (
    (response as ExportResponse).export_id === undefined &&
    (response as GenerationInFlightResponse).in_flight_export_id !== undefined
  );
}

/**
 * Poll `/status` until the export reaches a terminal state, and return it.
 *
 * TASK-877: an export that has not finished carries no `pptx_url`, and the Export
 * button treated every response as a success — the advisor was told "Portfolio deck
 * ready" by a turn that produced no deck. Waiting for the file is what makes the
 * control produce one; this is the waiting half.
 *
 * Gives up after `timeoutMs` and returns the last non-terminal response rather than
 * throwing, so the caller can say "still generating" — which is true — instead of
 * either claiming success or reporting a failure that has not happened.
 *
 * **A failed poll is not a failed export.** Review caught the first cut letting any
 * rejection from `/status` propagate: the gateway proxies that route with a 30s timeout
 * and can 502 under load, and a just-created row can 404 briefly. Either would have
 * surfaced as `Export failed: Gateway error` on a deck that was still being produced —
 * the same false verdict this module exists to prevent, arriving from the other side.
 * Transient errors are therefore swallowed until the deadline; only a total failure to
 * ever read the status rethrows, because inventing a state we never observed would be
 * the opposite mistake.
 */
export async function waitForExport(
  exportId: string,
  options: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {}
): Promise<ExportResponse> {
  const { intervalMs = 3000, timeoutMs = 300000, signal } = options;
  const deadline = Date.now() + timeoutMs;

  let lastError: unknown = null;

  // Returns the status, or null when this attempt could not read it. Assigning through a
  // closure confused TS's narrowing, hence a plain return value.
  const poll = async (): Promise<ExportResponse | null> => {
    try {
      const seen = await getExportStatus(exportId);
      lastError = null;
      return seen;
    } catch (err) {
      lastError = err;
      return null;
    }
  };

  let last: ExportResponse | null = await poll();
  while ((last === null || !isTerminalExportStatus(last.status)) && Date.now() < deadline) {
    if (signal?.aborted) break;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    if (signal?.aborted) break;
    const seen = await poll();
    // Keep the last GOOD reading: a blip must not erase what we already know.
    if (seen !== null) last = seen;
  }

  if (last === null) {
    // Every attempt failed — we know nothing, so say nothing. The caller's error path is
    // correct here in a way it would not be for a poll that merely blipped.
    throw lastError ?? new Error('export status unavailable');
  }
  return last;
}

/**
 * TASK-599: generate the FINSA suitability statement for a documented meeting.
 * Backend returns 202 {status: 'pending_documentation'} when the meeting has no
 * FINSA documentation yet; the PDF is the primary artefact served on download.
 */
export async function createFinsaExport(
  sessionId: string,
  language: string = 'en',
): Promise<ExportResponse | { status: string; message?: string }> {
  const url = `/api/exports/pptx/finsa/${encodeURIComponent(sessionId)}`;
  return await httpClientLongRunning.post<ExportResponse | { status: string; message?: string }>(
    url,
    { language }
  );
}

/**
 * Build a presigned URL the browser can hit directly. Backend returns 302
 * with the presigned MinIO URL — we resolve to the final URL by calling the
 * gateway and reading the redirect target (axios follows redirects by default).
 * Simpler: just point the <a href> directly at the gateway path; the browser
 * follows the redirect on click.
 */
export function getBriefPptxDownloadHref(exportId: string): string {
  return `/api/exports/${encodeURIComponent(exportId)}/download`;
}

export function getBriefPdfPreviewHref(exportId: string): string {
  return `/api/exports/${encodeURIComponent(exportId)}/preview`;
}

/**
 * Poll the export status until it reaches ready/failed/superseded or the
 * signal aborts. Used after a generation request returns a still-processing
 * status (today this is rare — the POST is synchronous — but kept for
 * resilience and future async modes).
 */
export async function pollExportUntilReady(
  exportId: string,
  options: { intervalMs?: number; maxAttempts?: number; signal?: AbortSignal } = {}
): Promise<ExportResponse> {
  const intervalMs = options.intervalMs ?? 1500;
  const maxAttempts = options.maxAttempts ?? 60;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (options.signal?.aborted) {
      throw new DOMException('aborted', 'AbortError');
    }
    const status = await getExportStatus(exportId);
    if (status.status === 'ready' || status.status === 'failed' || status.status === 'superseded') {
      return status;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error(`export ${exportId} did not reach a terminal state within ${maxAttempts} polls`);
}

// ============================================================================
// Portfolio export (TASK-407)
// ============================================================================

export type PortfolioExportView = 'client' | 'full';

export interface PortfolioExportOptions {
  portfolio_id?: string;
  as_of_date?: string;
  language?: 'en' | 'de' | 'fr' | 'it';
  view?: PortfolioExportView;
  regenerate?: boolean;
  preview?: boolean;
  session_id?: string;
  client_name?: string;
  signal?: AbortSignal;
}

export interface MultiplePortfoliosErrorPayload {
  error: 'multiple_portfolios';
  message: string;
  portfolios: Array<{ id: string; name?: string; [key: string]: unknown }>;
}

/**
 * Thrown when the backend reports the client has >1 portfolio and the caller
 * didn't pin `portfolio_id`. The caller should show a portfolio picker and
 * retry with the user's choice.
 */
export class MultiplePortfoliosError extends Error {
  readonly response: MultiplePortfoliosErrorPayload;
  constructor(response: MultiplePortfoliosErrorPayload) {
    super(response.message || 'multiple portfolios; portfolio_id required');
    this.name = 'MultiplePortfoliosError';
    this.response = response;
  }
}

function isMultiplePortfoliosError(detail: unknown): detail is MultiplePortfoliosErrorPayload {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    (detail as { error?: unknown }).error === 'multiple_portfolios'
  );
}

/**
 * Generate (or fetch cached) a portfolio PPTX export for `clientId`.
 *
 * Throws `MultiplePortfoliosError` when the client has multiple portfolios
 * and `portfolio_id` was not provided — caller should show a picker.
 *
 * TASK-877: posts with `httpClientLongRunning`. The endpoint generates
 * SYNCHRONOUSLY — `create_portfolio_export` awaits an LLM code-gen plus a sandbox
 * run inside the request — and a measured run against the live service was still
 * going at 120s. On `httpClient`'s 30s default the request could never survive to
 * a response, which is why the Export button was recorded as producing "no menu,
 * no network request, no download". `regeneratePortfolioExport` below already
 * posted to this same URL with the long-running client; only the button's path
 * was left on the default one.
 */
export async function getOrCreatePortfolioExport(
  clientId: string,
  options: PortfolioExportOptions = {}
): Promise<ExportResponse> {
  const {
    portfolio_id,
    as_of_date,
    language = 'en',
    view = 'client',
    regenerate = false,
    preview = false,
    session_id,
    client_name,
    signal,
  } = options;
  const url = `/api/exports/pptx/portfolio/${encodeURIComponent(clientId)}?preview=${preview}`;
  try {
    return await httpClientLongRunning.post<ExportResponse>(
      url,
      { portfolio_id, as_of_date, language, view, regenerate, session_id, client_name },
      { signal }
    );
  } catch (err) {
    // axios-style error surfaces backend detail under .response.data.detail
    const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (isMultiplePortfoliosError(detail)) {
      throw new MultiplePortfoliosError(detail);
    }
    throw err;
  }
}

/** Returns the current ready portfolio export for (client, language) or null. */
export async function getPortfolioExportLatest(
  clientId: string,
  language: string = 'en'
): Promise<ExportResponse | null> {
  const url = `/api/exports/pptx/portfolio/${encodeURIComponent(
    clientId
  )}/latest?language=${encodeURIComponent(language)}`;
  return await httpClient.get<ExportResponse | null>(url);
}

/** Same download endpoint as brief — keyed by export_id. Aliased for readability. */
export function getPortfolioPptxDownloadHref(exportId: string): string {
  return `/api/exports/${encodeURIComponent(exportId)}/download`;
}

export function getPortfolioPdfPreviewHref(exportId: string): string {
  return `/api/exports/${encodeURIComponent(exportId)}/preview`;
}

/** TASK-741: generic download href for any ready export (same endpoint family). */
export function getExportDownloadHref(exportId: string): string {
  return `/api/exports/${encodeURIComponent(exportId)}/download`;
}
